import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Play, Pause, Volume2 } from "lucide-react";
import {
  VoiceConfig,
  getVoicesByLanguage,
} from "@packages/config/elevenlabsConfig";
import { CreatePronunciationAssignmentRequestDtoAccent } from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/index";

interface VoiceSelectorProps {
  selectedVoiceId?: string;
  onVoiceChange: (voiceId: string) => void;
  language: "en" | "de";
  accent: CreatePronunciationAssignmentRequestDtoAccent;
  className?: string;
  themeColor?: "blue" | "purple" | "green" | "orange" | "red" | "teal";
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  selectedVoiceId,
  onVoiceChange,
  language,
  accent,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Get filtered voices based on language and accent
  const getFilteredVoices = (): VoiceConfig[] => {
    const languageKey =
      language === "en" ? (accent === "AMERICAN" ? "en_us" : "en_gb") : "de_de";
    return getVoicesByLanguage(languageKey);
  };

  const filteredVoices = getFilteredVoices();
  const selectedVoice =
    filteredVoices.find((voice) => voice.voiceId === selectedVoiceId) ||
    filteredVoices[0];

  const handlePlayVoice = (voice: VoiceConfig, e: React.MouseEvent) => {
    e.stopPropagation();

    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // If clicking the same voice that's playing, stop it
    if (playingVoiceId === voice.voiceId) {
      setPlayingVoiceId(null);
      return;
    }

    // Start new audio
    const audio = new Audio(voice.voiceSamplePath);
    audioRef.current = audio;
    setPlayingVoiceId(voice.voiceId);

    audio.addEventListener("ended", () => {
      setPlayingVoiceId(null);
    });

    audio.play().catch(console.error);
  };

  const handleVoiceSelect = (voiceId: string) => {
    onVoiceChange(voiceId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={popoverRef}>
      {/* Trigger Button - matching OutputLanguageSelector style */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        rounded="lg"
        bgColor="bg-white/50 dark:bg-dark-500 hover:bg-white/70 dark:hover:bg-gray-800/70"
        textColor="text-gray-600 dark:text-gray-300"
        size="md"
        className="flex items-center justify-between gap-2 border border-gray-200 dark:border-dark-400 backdrop-blur-sm px-4 py-3 w-full"
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <Volume2 className="w-3 h-3 text-gray-500 dark:text-gray-400" />
          </div>
          <span className="text-sm font-medium">
            {selectedVoice?.displayName || t("voiceSelector.selectVoice")}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden z-[150] border border-gray-100 dark:border-gray-700">
          <ul className="py-1">
            {filteredVoices.map((voice) => (
              <li key={voice.voiceId}>
                <button
                  onClick={() => handleVoiceSelect(voice.voiceId)}
                  className={`
                    flex items-center justify-between w-full px-4 py-2 text-sm text-left transition-colors
                    ${
                      voice.voiceId === selectedVoiceId
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <Volume2 className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <span className="font-medium">{voice.displayName}</span>
                    {voice.accent && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {voice.accent === "AMERICAN"
                          ? t("pronunciationAssessment.create.accent.american")
                          : t("pronunciationAssessment.create.accent.british")}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => handlePlayVoice(voice, e)}
                    className="flex items-center gap-1.5 px-3 py-1 text-sm rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-500"
                  >
                    {playingVoiceId === voice.voiceId ? (
                      <>
                        <Pause className="w-3.5 h-3.5" />
                        <span>Stop</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-3.5 h-3.5" />
                        <span>Preview</span>
                      </>
                    )}
                  </button>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VoiceSelector;
